import { Check, LinkIcon, Loader2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { useEntityFileContext } from "@/react/components/entityViews/files/context/EntityFileContext";
import { cn } from "@/modern/utils";

interface CompanyCamProjectStatusLinkProps {
  className?: string;
}

/**
 * Compact CC project chip with import spinner / connected check.
 * Shared by Task Media/Documents header and the default file toolbar meta row.
 */
export function CompanyCamProjectStatusLink({ className }: CompanyCamProjectStatusLinkProps) {
  const { currentCCProject, entityRecord, isImportingCompanyCam } = useEntityFileContext();

  const companyCamProjectId = entityRecord.companyCamProject as
    | number
    | string
    | null
    | undefined;
  const companyCamName =
    currentCCProject?.name ||
    (companyCamProjectId ? `Project ${companyCamProjectId}` : null);

  if (!companyCamProjectId || !companyCamName) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <a
          href={`https://app.companycam.com/projects/${companyCamProjectId}`}
          rel="noopener noreferrer"
          target="_blank"
          aria-label={
            isImportingCompanyCam
              ? `Importing from CompanyCam project ${companyCamName}`
              : `Open CompanyCam project ${companyCamName}`
          }
          className={cn(
            "inline-flex max-w-[16rem] items-center gap-1.5 transition-colors hover:text-foreground",
            className,
          )}
        >
          <span className="shrink-0 tracking-wide">CC</span>
          <LinkIcon className="size-3 shrink-0 opacity-70" aria-hidden />
          <span className="min-w-0 truncate">{companyCamName}</span>
          {isImportingCompanyCam ? (
            <Loader2
              className="size-3.5 shrink-0 animate-spin text-muted-foreground"
              aria-hidden
            />
          ) : (
            <Check className="size-3.5 shrink-0 text-success" aria-hidden />
          )}
          <span className="sr-only">
            {isImportingCompanyCam ? "Importing…" : "Connected"}
          </span>
        </a>
      </TooltipTrigger>
      <TooltipContent>
        {isImportingCompanyCam
          ? "Importing…"
          : `Open CompanyCam project: ${companyCamName}`}
      </TooltipContent>
    </Tooltip>
  );
}
